import { Controller } from "@hotwired/stimulus"

// Browser Web Push subscription management.
//
// Wired up from app/views/account/_push_settings.html.slim. The VAPID public
// key is read from the <meta name="vapid-public-key"> tag emitted in the
// application layout (set when Rails.application.credentials.vapid is
// configured — see app/helpers/application_helper.rb#vapid_public_key).
//
// Flow:
//   register()   — requestPermission → SW ready → pushManager.subscribe →
//                  POST /push_subscriptions  (idempotent on user_id+endpoint)
//   unregister() — SW.getRegistration → sub.unsubscribe →
//                  DELETE /push_subscriptions/destroy_by_endpoint
//
// Status updates render into the `status` target so the user gets feedback
// without needing browser devtools.
export default class extends Controller {
  static targets = ["status"]

  connect() {
    if (!this.supported()) {
      this.setStatus("This browser does not support push notifications.")
    }
  }

  async register(event) {
    event && event.preventDefault()

    if (!this.supported()) {
      this.setStatus("This browser does not support push notifications.")
      return
    }

    const vapidKey = this.vapidPublicKey()
    if (!vapidKey) {
      this.setStatus("Push is not configured on the server.")
      return
    }

    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.setStatus("Notifications are blocked. Allow them in your browser settings to enable push.")
        return
      }

      // Idempotent: re-registering the worker is a no-op if it's already
      // installed at the same scope.
      await navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
      const registration = await navigator.serviceWorker.ready

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey),
      })

      const keys = subscription.toJSON().keys || {}
      const response = await fetch("/push_subscriptions", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({
          push_subscription: {
            endpoint: subscription.endpoint,
            p256dh_key: keys.p256dh,
            auth_key: keys.auth,
          },
        }),
      })

      if (response.ok) {
        this.setStatus("Push notifications enabled on this device.")
      } else {
        this.setStatus(`Could not save subscription (status ${response.status}).`)
      }
    } catch (err) {
      console.error("Push registration failed:", err)
      this.setStatus(`Could not enable push: ${err.message || err}`)
    }
  }

  async unregister(event) {
    event && event.preventDefault()

    if (!this.supported()) {
      this.setStatus("This browser does not support push notifications.")
      return
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      const subscription = registration ? await registration.pushManager.getSubscription() : null

      if (!subscription) {
        this.setStatus("This device wasn't subscribed.")
        return
      }

      const endpoint = subscription.endpoint
      await subscription.unsubscribe()

      const url = `/push_subscriptions/destroy_by_endpoint?endpoint=${encodeURIComponent(endpoint)}`
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
      })

      if (response.ok) {
        this.setStatus("Push notifications disabled on this device.")
      } else {
        this.setStatus(`Could not remove subscription (status ${response.status}).`)
      }
    } catch (err) {
      console.error("Push unregistration failed:", err)
      this.setStatus(`Could not disable push: ${err.message || err}`)
    }
  }

  // ---- helpers ----

  supported() {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    )
  }

  setStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }

  csrfToken() {
    const tag = document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.getAttribute("content") : ""
  }

  vapidPublicKey() {
    const tag = document.querySelector('meta[name="vapid-public-key"]')
    return tag ? tag.getAttribute("content") : null
  }

  // VAPID public keys are URL-safe base64; pushManager.subscribe wants a
  // Uint8Array of the raw bytes.
  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
